/**
 * Multiplayer Connect Four Service
 *
 * Extends GenericMultiplayerService for Connect Four-specific multiplayer.
 * Handles room creation/joining, move sync, reconnection with exponential backoff.
 *
 * Requirements: 22.1–22.7, 23.1, 24.1–24.5, 25.1–25.7, 27.1–27.3,
 *               28.4, 29.1–29.6, 30.1–30.5
 */

import { WebSocketService } from '../../services/WebSocketService';
import {
  GenericMultiplayerService,
  GameResult,
} from '../../services/multiplayer/GenericMultiplayerService';
import {
  ConnectFourGameState,
  ConnectFourMove,
  DiscColor,
} from '../types';

const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_ATTEMPTS = 3;
const GAME_STATE_RETENTION_MS = 5 * 60 * 1000; // 5 minutes

export class MultiplayerConnectFourService extends GenericMultiplayerService<
  ConnectFourGameState,
  ConnectFourMove
> {
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private disconnectedAt: number | null = null;
  private onReconnectionFailedCallback?: () => void;

  constructor(wsService?: WebSocketService) {
    super(wsService ?? WebSocketService.getInstance(), 'connect-four');
  }

  // -----------------------------------------------------------------------
  // Abstract method implementations
  // -----------------------------------------------------------------------

  protected setupMessageHandlers(): void {
    this.wsService.setCustomMessageHandler((message: any) => {
      try {
        const type: string = message.type ?? '';

        switch (type) {
          case 'connect-four:roomCreated':
            this.roomCode = message.roomCode ?? null;
            this.playerColor = message.color ?? null;
            this.playerRole = message.color ?? 'red';
            if (this.onGameStartedCallback) {
              this.onGameStartedCallback();
            }
            break;

          case 'connect-four:roomJoined':
            this.roomCode = message.roomCode ?? null;
            this.playerColor = message.color ?? null;
            this.playerRole = message.color ?? 'yellow';
            if (this.onGameStartedCallback) {
              this.onGameStartedCallback();
            }
            break;

          case 'connect-four:opponentJoined':
            if (this.onPlayerConnectedCallback) {
              this.onPlayerConnectedCallback(message.playerId ?? 'opponent');
            }
            if (this.onGameStartedCallback) {
              this.onGameStartedCallback();
            }
            break;

          case 'connect-four:moveUpdate':
            if (message.gameState) {
              this.gameState = message.gameState;
            }
            if (this.onMoveUpdateCallback) {
              this.onMoveUpdateCallback(message.move, message.gameState);
            }
            break;

          case 'connect-four:gameOver': {
            const result: GameResult = {
              result: message.result ?? 'win',
              winner: message.winner,
              reason: message.reason,
            };
            if (this.onGameOverCallback) {
              this.onGameOverCallback(result);
            }
            break;
          }

          case 'connect-four:playerDisconnected':
            if (this.onPlayerDisconnectedCallback) {
              this.onPlayerDisconnectedCallback(message.playerId ?? 'opponent');
            }
            break;

          case 'connect-four:playerReconnected':
            if (this.onPlayerConnectedCallback) {
              this.onPlayerConnectedCallback(message.playerId ?? 'opponent');
            }
            break;

          case 'connect-four:gameState':
            if (message.gameState) {
              this.gameState = message.gameState;
            }
            if (this.onGameStateReceivedCallback) {
              this.onGameStateReceivedCallback(message.gameState);
            }
            this.resetReconnectionState();
            if (this.onConnectionStatusCallback) {
              this.onConnectionStatusCallback('connected');
            }
            break;

          case 'connect-four:error':
            this.handleError(message.message ?? message.error ?? 'Unknown error');
            break;

          default:
            break;
        }
      } catch (err) {
        this.handleError('Error handling connect-four message', err);
      }
    });
  }

  protected sendCreateRoomMessage(): void {
    this.wsService.send({
      action: 'connect-four:createRoom',
      playerId: this.playerId,
    });
  }

  protected sendJoinRoomMessage(roomCode: string): void {
    this.wsService.send({
      action: 'connect-four:joinRoom',
      roomCode,
      playerId: this.playerId,
    });
  }

  protected sendLeaveRoomMessage(): void {
    if (this.roomCode) {
      this.wsService.send({
        action: 'connect-four:leaveRoom',
        roomCode: this.roomCode,
        playerId: this.playerId,
      });
    }
  }

  protected getMaxPlayers(): number {
    return 2;
  }

  // -----------------------------------------------------------------------
  // Move sending
  // -----------------------------------------------------------------------

  async sendMove(move: ConnectFourMove): Promise<boolean> {
    try {
      if (!this.roomCode) {
        throw new Error('Not in a multiplayer game');
      }

      this.wsService.send({
        action: 'connect-four:move',
        roomCode: this.roomCode,
        playerId: this.playerId,
        move: {
          column: move.column,
          player: move.player,
        },
        gameState: this.gameState,
      });

      return true;
    } catch (error) {
      this.handleError('Failed to send move', error);
      return false;
    }
  }

  // -----------------------------------------------------------------------
  // Turn check
  // -----------------------------------------------------------------------

  isMyTurn(currentTurn: string): boolean {
    return this.playerColor === currentTurn;
  }

  // -----------------------------------------------------------------------
  // Connect Four-specific actions
  // -----------------------------------------------------------------------

  async resign(): Promise<void> {
    if (!this.roomCode) {
      throw new Error('Not in a multiplayer game');
    }
    this.wsService.send({
      action: 'connect-four:resign',
      roomCode: this.roomCode,
      playerId: this.playerId,
    });
  }

  getPlayerColor(): DiscColor | null {
    return this.playerColor as DiscColor | null;
  }

  // -----------------------------------------------------------------------
  // Connection management & exponential backoff reconnection
  // -----------------------------------------------------------------------

  handleDisconnect(): void {
    this.disconnectedAt = Date.now();

    if (this.onConnectionStatusCallback) {
      this.onConnectionStatusCallback('disconnected');
    }

    this.attemptReconnection();
  }

  private attemptReconnection(): void {
    if (this.reconnectAttempts >= RECONNECT_MAX_ATTEMPTS) {
      if (this.onReconnectionFailedCallback) {
        this.onReconnectionFailedCallback();
      }
      this.handleError('Max reconnection attempts reached. Please reconnect manually or return to mode selection.');
      return;
    }

    const delay = RECONNECT_BASE_DELAY_MS * Math.pow(2, this.reconnectAttempts);
    this.reconnectAttempts++;

    if (this.onConnectionStatusCallback) {
      this.onConnectionStatusCallback('reconnecting');
    }

    this.reconnectTimer = setTimeout(async () => {
      try {
        await this.wsService.connect();

        if (this.roomCode && this.playerId) {
          this.wsService.send({
            action: 'connect-four:reconnect',
            roomCode: this.roomCode,
            playerId: this.playerId,
          });
        }
      } catch {
        this.attemptReconnection();
      }
    }, delay);
  }

  isGameStateExpired(): boolean {
    if (this.disconnectedAt === null) return false;
    return Date.now() - this.disconnectedAt > GAME_STATE_RETENTION_MS;
  }

  getReconnectAttempts(): number {
    return this.reconnectAttempts;
  }

  // -----------------------------------------------------------------------
  // Event handler setters
  // -----------------------------------------------------------------------

  onReconnectionFailed(callback: () => void): void {
    this.onReconnectionFailedCallback = callback;
  }

  // -----------------------------------------------------------------------
  // Cleanup
  // -----------------------------------------------------------------------

  protected cleanup(): void {
    this.resetReconnectionState();
    this.disconnectedAt = null;
    super.cleanup();
  }

  private resetReconnectionState(): void {
    this.reconnectAttempts = 0;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}
